import { useState } from 'react';
import { Button } from "@tonic/shared/components/ui";
import { Input } from "@tonic/shared/components/ui";
import { Label } from "@tonic/shared/components/ui";
import { 
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from "@tonic/shared/components/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@tonic/shared/components/ui";
import { Switch } from "@tonic/shared/components/ui";
import { useUpdateProduct } from '../../../hooks/useProduct';
import PropTypes from 'prop-types';

export function ReCommerceTab({ product }) {
  const updateMutation = useUpdateProduct(product.id);
  const [attributes, setAttributes] = useState({
    eligibleForResale: product.reCommerceAttributes?.eligibleForResale || false,
    eligibleForRepair: product.reCommerceAttributes?.eligibleForRepair || false,
    eligibleForRental: product.reCommerceAttributes?.eligibleForRental || false,
    isRefillable: product.reCommerceAttributes?.isRefillable || false,
    durabilityRating: product.reCommerceAttributes?.durabilityRating || '',
    repairDifficulty: product.reCommerceAttributes?.repairDifficulty || '',
    maxRentalCycles: product.reCommerceAttributes?.maxRentalCycles || ''
  });

  const handleToggleChange = (field) => {
    setAttributes(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleNumberChange = (field, value) => {
    setAttributes(prev => ({
      ...prev,
      [field]: value === '' ? '' : parseInt(value)
    }));
  };

  const handleDifficultyChange = (value) => {
    setAttributes(prev => ({
      ...prev,
      repairDifficulty: value
    }));
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        reCommerceAttributes: {
          ...attributes,
          durabilityRating: attributes.durabilityRating === '' ? null : parseInt(attributes.durabilityRating),
          maxRentalCycles: attributes.maxRentalCycles === '' ? null : parseInt(attributes.maxRentalCycles)
        }
      });
      // TODO: Show success toast
    } catch (error) {
      console.error('Failed to update product:', error);
      // TODO: Show error toast
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Re-Commerce Eligibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Eligible for Resale</Label>
                <p className="text-sm text-muted-foreground">
                  Can this product be resold after use?
                </p>
              </div>
              <Switch
                checked={attributes.eligibleForResale}
                onCheckedChange={() => handleToggleChange('eligibleForResale')}
                disabled={updateMutation.isPending}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Eligible for Repair</Label>
                <p className="text-sm text-muted-foreground">
                  Can this product be repaired?
                </p>
              </div>
              <Switch
                checked={attributes.eligibleForRepair}
                onCheckedChange={() => handleToggleChange('eligibleForRepair')}
                disabled={updateMutation.isPending}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Eligible for Rental</Label>
                <p className="text-sm text-muted-foreground">
                  Can this product be rented out?
                </p>
              </div>
              <Switch
                checked={attributes.eligibleForRental}
                onCheckedChange={() => handleToggleChange('eligibleForRental')}
                disabled={updateMutation.isPending}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Refillable</Label>
                <p className="text-sm text-muted-foreground">
                  Is this a refillable container?
                </p>
              </div>
              <Switch
                checked={attributes.isRefillable}
                onCheckedChange={() => handleToggleChange('isRefillable')}
                disabled={updateMutation.isPending}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Re-Commerce Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="durabilityRating">Durability Rating</Label>
              <Input
                id="durabilityRating"
                type="number"
                min="1"
                max="10"
                value={attributes.durabilityRating}
                onChange={(e) => handleNumberChange('durabilityRating', e.target.value)}
                placeholder="Rate from 1-10"
                disabled={updateMutation.isPending}
              />
              <p className="text-sm text-muted-foreground">
                Rate from 1-10, where 10 is most durable
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="repairDifficulty">Repair Difficulty</Label>
              <Select
                value={attributes.repairDifficulty}
                onValueChange={handleDifficultyChange}
                disabled={updateMutation.isPending}
              >
                <SelectTrigger id="repairDifficulty">
                  <SelectValue placeholder="Select difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                How difficult is it to repair this product?
              </p>
            </div>
            {attributes.eligibleForRental && (
              <div className="space-y-2">
                <Label htmlFor="maxRentalCycles">Maximum Rental Cycles</Label>
                <Input
                  id="maxRentalCycles"
                  type="number"
                  min="0"
                  value={attributes.maxRentalCycles}
                  onChange={(e) => handleNumberChange('maxRentalCycles', e.target.value)}
                  placeholder="Optional"
                  disabled={updateMutation.isPending}
                />
                <p className="text-sm text-muted-foreground">
                  Maximum number of times this product can be rented
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-2">
        <Button 
          type="button" 
          variant="outline"
          disabled={updateMutation.isPending}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

ReCommerceTab.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    reCommerceAttributes: PropTypes.shape({
      eligibleForResale: PropTypes.bool,
      eligibleForRepair: PropTypes.bool,
      eligibleForRental: PropTypes.bool,
      isRefillable: PropTypes.bool,
      durabilityRating: PropTypes.number,
      repairDifficulty: PropTypes.oneOf(['easy', 'medium', 'hard']),
      maxRentalCycles: PropTypes.number
    })
  }).isRequired
}; 